/**
 * UI-only feature gating (menus, routes, FeatureGate).
 *
 * SECURITY WARNINGS:
 * - H-2: Permissions cached in localStorage are for presentation only
 * - H-3: PII (email, company_name) should NOT be stored in localStorage (GDPR violation)
 * - Authorization is enforced by backend on every API request
 * - Tampering with client storage must not grant API access
 * - Server-side RBAC is the security control (accepted risk)
 *
 * SECURITY FIX H-3: Store only minimal non-PII data in localStorage.
 * Email and company_name should be fetched from server when needed or stored in sessionStorage.
 */
import PropTypes from "prop-types";
import { LOCAL_STORAGE_KEYS } from "./localStorageUtil";
import { eraseAuthCookies } from "./cookieUtil";
import { addLog } from "./persistentLogger";

const LEGACY_PERMISSIONS_KEY = "USER_PERMISSIONS";
const LEGACY_USER_DETAILS_KEY = "IQEVSE_FLEET_CSMS_user_details";

export const FEATURES = {
  FLEET_SEARCH: "550e8400e29b41d4",
  FLEET_EXPORT: "550e8400e29b41d5",
  CHARGER_METRICS: "550e8400e29b41d6",
  CHARGER_SUMMARY: "550e8400e29b41d7",
  CHARGER_REMOTE_START: "550e8400e29b41d8",
  CHARGER_REMOTE_STOP: "550e8400e29b41d9",
  OCPP_MESSAGES: "550e8400e29b41da",
  TROUBLESHOOTING: "550e8400e29b41db",
  USER_MANAGEMENT: "550e8400e29b41dc",
  SYSTEM_SETTINGS: "550e8400e29b41dd",
  COMMANDS: "550e8400e29b41de",
};

/**
 * Maps legacy UI feature IDs to RBAC permission strings (feature:action).
 * Used to check the new permissions set when the old featureId-based check is called.
 */
const FEATURE_TO_PERMISSION = {
  [FEATURES.FLEET_SEARCH]: "fleet:read",
  [FEATURES.FLEET_EXPORT]: "fleet:read",
  [FEATURES.CHARGER_METRICS]: "chargers:read",
  [FEATURES.CHARGER_SUMMARY]: "chargers:read",
  [FEATURES.CHARGER_REMOTE_START]: "charger_remote:execute",
  [FEATURES.CHARGER_REMOTE_STOP]: "charger_remote:execute",
  [FEATURES.OCPP_MESSAGES]: "ocpp_messages:read",
  [FEATURES.TROUBLESHOOTING]: "troubleshooting:read",
  [FEATURES.USER_MANAGEMENT]: "user_management:write",
  [FEATURES.SYSTEM_SETTINGS]: "user_management:write",
  [FEATURES.COMMANDS]: "commands:execute",
};

export const ROLE = {
  ADMIN: "ADMIN",
  READ: "READ",
  CUSTOM: "CUSTOM",
};

function migrateLegacyPermissions() {
  try {
    const legacy = localStorage.getItem(LEGACY_PERMISSIONS_KEY);
    if (!legacy) return null;
    if (!localStorage.getItem(LOCAL_STORAGE_KEYS.FEATURE_ACCESS)) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.FEATURE_ACCESS, legacy);
    }
    localStorage.removeItem(LEGACY_PERMISSIONS_KEY);
    return JSON.parse(legacy);
  } catch {
    localStorage.removeItem(LEGACY_PERMISSIONS_KEY);
    return null;
  }
}

function readStoredPermissions() {
  migrateLegacyPermissions();
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.FEATURE_ACCESS);
    return stored ? JSON.parse(stored) : null;
  } catch {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.FEATURE_ACCESS);
    return null;
  }
}

function removeStoredPermissions() {
  localStorage.removeItem(LOCAL_STORAGE_KEYS.FEATURE_ACCESS);
  localStorage.removeItem(LEGACY_PERMISSIONS_KEY);
}

/** Removes legacy user-details keys only (H-2); does not clear FEATURE_ACCESS. */
export function clearLegacyUserDetailsStorage() {
  try {
    localStorage.removeItem(LEGACY_USER_DETAILS_KEY);
    localStorage.removeItem(LEGACY_PERMISSIONS_KEY);
  } catch {
    // ignore
  }
}

/**
 * SECURITY FIX H-5: Safe redirect with validation to prevent open redirect attacks
 *
 * @param {string} returnUrl - Optional return URL (must be relative)
 */
function safeRedirect(url) {
  // Only allow relative URLs
  if (!url || !url.startsWith("/")) {
    return "/login";
  }

  // Block protocol-relative URLs (//evil.com)
  if (url.startsWith("//")) {
    return "/login";
  }

  // Block data: and javascript: URLs
  if (url.match(/^(data|javascript|vbscript):/i)) {
    return "/login";
  }

  // Block encoded attempts
  if (url.includes("%2F%2F") || url.includes("%3A")) {
    return "/login";
  }

  return url;
}

export function redirectToLogin(returnUrl) {
  if (!window.location.pathname.startsWith("/login")) {
    const safeUrl = returnUrl ? safeRedirect(returnUrl) : "/login";
    window.location.href = safeUrl;
  }
}

class FeatureManager {
  constructor() {
    this.permissions = null;
  }

  init(loginResponse) {
    this.permissions = loginResponse.user_permissions;
    if (this.permissions) {
      // SECURITY FIX H-3: Store only non-PII data in localStorage
      const minimalData = {
        role_id: this.permissions.role_id || this.permissions.role || "",
        // Legacy fields for backward compat
        role: this.permissions.role_id || this.permissions.role || "",
        allowed_features: this.permissions.allowed_features || [],
        denied_features: this.permissions.denied_features || [],
        // RBAC permissions set (e.g. ["fleet:read", "chargers:write", ...])
        permissions: this.permissions.permissions || [],
        denied_permissions: this.permissions.denied_permissions || [],
      };
      localStorage.setItem(LOCAL_STORAGE_KEYS.FEATURE_ACCESS, JSON.stringify(minimalData));

      // Store PII in sessionStorage (cleared on tab close)
      if (
        this.permissions.email ||
        this.permissions.company_name ||
        this.permissions.organization ||
        this.permissions.name
      ) {
        const piiData = {
          email: this.permissions.email || "",
          company_name: this.permissions.company_name || this.permissions.organization || "",
          name: this.permissions.name || "",
        };
        sessionStorage.setItem("USER_PII", JSON.stringify(piiData));
      }
    }
    localStorage.removeItem(LEGACY_PERMISSIONS_KEY);
  }

  load() {
    if (this.permissions) return;
    this.permissions = readStoredPermissions();
  }

  /**
   * Check if a feature is allowed. Uses RBAC permissions set when available,
   * falls back to legacy allowed_features/denied_features lists.
   */
  isAllowed(featureId) {
    if (!this.permissions) this.load();
    if (!this.permissions) {
      this.handleMissingPermissions();
      return false;
    }

    const { permissions = [], denied_permissions = [] } = this.permissions;

    // If we have RBAC permissions, use them
    if (permissions.length > 0 || denied_permissions.length > 0) {
      // Wildcard *:* grants access to everything
      if (permissions.includes("*:*")) return true;
      const requiredPerm = FEATURE_TO_PERMISSION[featureId];
      if (!requiredPerm) return false; // Unknown feature — deny by default (least-privilege)
      if (denied_permissions.includes(requiredPerm)) return false;
      return permissions.includes(requiredPerm);
    }

    // Legacy fallback: role + allowed_features / denied_features
    const { allowed_features = [], denied_features = [], role } = this.permissions;

    if (featureId === FEATURES.USER_MANAGEMENT || featureId === FEATURES.SYSTEM_SETTINGS) {
      return role === ROLE.ADMIN;
    }

    if (
      role?.toUpperCase() === ROLE.READ &&
      (featureId === FEATURES.TROUBLESHOOTING || featureId === FEATURES.COMMANDS)
    ) {
      return false;
    }

    if (denied_features.includes(featureId)) return false;
    if (allowed_features.length === 0) return true;
    return allowed_features.includes(featureId);
  }

  /**
   * Check if a specific RBAC permission is granted (e.g. "user_management:write").
   */
  hasPermission(permission) {
    if (!this.permissions) this.load();
    if (!this.permissions) return false;
    const { permissions = [], denied_permissions = [] } = this.permissions;
    if (permissions.includes("*:*")) return true;
    if (denied_permissions.includes(permission)) return false;
    return permissions.includes(permission);
  }

  isAdmin() {
    if (!this.permissions) this.load();
    if (!this.permissions) {
      this.handleMissingPermissions();
      return false;
    }
    // Check RBAC permissions first — true admin has wildcard access (*:*)
    const { permissions = [] } = this.permissions;
    if (permissions.length > 0) {
      return permissions.includes("*:*");
    }
    // Legacy fallback
    return this.permissions?.role === ROLE.ADMIN;
  }

  getRoleId() {
    if (!this.permissions) this.load();
    return this.permissions?.role_id || this.permissions?.role || "";
  }

  clear() {
    this.permissions = null;
    removeStoredPermissions();
  }

  handleMissingPermissions() {
    this.clear();
    redirectToLogin();
  }
}

const featureManager = new FeatureManager();

export const initFeatureAccess = loginResponse => featureManager.init(loginResponse);
export const clearFeatureAccess = () => featureManager.clear();
export const checkFeatureAccess = featureId => featureManager.isAllowed(featureId);
export const checkPermission = permission => featureManager.hasPermission(permission);
export const getRoleId = () => featureManager.getRoleId();

export function getSessionProfile() {
  // SECURITY FIX H-3: Read PII from sessionStorage (not localStorage)
  try {
    const piiData = sessionStorage.getItem("USER_PII");
    if (piiData) {
      const parsed = JSON.parse(piiData);
      return {
        email: parsed.email || "",
        companyName: parsed.company_name || "My Company",
        displayName: parsed.name || parsed.email?.split("@")[0] || "User",
      };
    }
  } catch {
    // Fall back to default
  }

  return { email: "", companyName: "My Company", displayName: "User" };
}

export function getCompanyDisplayName() {
  return getSessionProfile().companyName;
}

export function isEnphaseUser() {
  // SECURITY FIX H-3: Read company from sessionStorage
  try {
    const piiData = sessionStorage.getItem("USER_PII");
    if (piiData) {
      const parsed = JSON.parse(piiData);
      return (parsed.company_name || "").toLowerCase() === "enphase";
    }
  } catch {
    // Fall back to false
  }
  return false;
}

export const useFeatureAccess = () => ({
  isAllowed: featureId => featureManager.isAllowed(featureId),
  isAdmin: () => featureManager.isAdmin(),
  hasPermission: permission => featureManager.hasPermission(permission),
  getRoleId: () => featureManager.getRoleId(),
});

export function handleUnauthorizedSession() {
  addLog("Auth", "Unauthorized session detected! Logging out...");

  featureManager.clear();
  clearLegacyUserDetailsStorage();
  eraseAuthCookies();

  // SECURITY FIX H-3: Clear PII from sessionStorage
  sessionStorage.removeItem("USER_PII");

  redirectToLogin();
}

export const FeatureGate = ({ featureId, children }) => {
  const { isAllowed } = useFeatureAccess();
  return isAllowed(featureId) ? children : null;
};

FeatureGate.propTypes = {
  featureId: PropTypes.string.isRequired,
  children: PropTypes.node,
};

export default featureManager;
